import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * Modified for DWELL/product page:
 * - Adds public selectMediaById(mediaId) method.
 * - Supports Color attribute -> main gallery sync.
 * - Selects the real slideshow slide by Shopify media id.
 * - Syncs thumbnail active/selected state.
 * - Centers the matched thumbnail inside its real scroll container.
 * - Uses boundary-clamped centering:
 *   - Middle thumbnails move to visual center.
 *   - First thumbnails stay near the start.
 *   - Last thumbnails stay near the end.
 *   - No blank space is created on either side.
 * - Prioritizes thumbnails with data-media-id / data-slide-index from snippets/slideshow-controls.liquid.
 * - Falls back to index matching when data-media-id is unavailable.
 * - Avoids accidentally selecting main media elements or zoom dialog thumbnails as normal thumbnails.
 * - Prevents page scroll jump when switching variant media.
 * - Does not use scrollIntoView for normal thumbnail centering, avoiding page jump.
 * - Keeps original productSelect replacement logic.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   *
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const index = Number(event.detail.index);

    if (Number.isNaN(index)) return;

    this.#selectSlideshowIndexWithoutPageJump(index, { animate: false });
    this.#syncActiveThumbnailByIndex(index, { center: true, animate: false });
  };

  /**
   * Selects media by Shopify media id.
   * Used by variant image swatches to sync:
   * Color option → main gallery → thumbnail active state → thumbnail boundary-clamped center.
   *
   * @param {string | number} mediaId
   * @param {{ animate?: boolean; centerThumbnail?: boolean }} [options]
   * @returns {boolean}
   */
  selectMediaById(mediaId, options = {}) {
    if (!mediaId) return false;

    const targetMediaId = String(mediaId);
    const mediaItems = this.#getMediaItems();

    let index = this.#findMediaIndexById(targetMediaId, mediaItems);

    if (index < 0) {
      const mediaElement = this.#findMediaElementById(targetMediaId);

      if (mediaElement instanceof HTMLElement) {
        const closestItem = this.#getClosestMediaItem(mediaElement);

        if (closestItem instanceof HTMLElement) {
          index = mediaItems.findIndex((item) => item === closestItem || item.contains(closestItem));
        }
      }
    }

    if (index < 0) return false;

    this.#selectSlideshowIndexWithoutPageJump(index, options);
    this.#syncActiveThumbnail(targetMediaId, index, {
      center: options.centerThumbnail !== false,
      animate: options.animate === true,
    });

    return true;
  }

  /**
   * Finds a media element by Shopify media id.
   *
   * @param {string} mediaId
   * @returns {HTMLElement | null}
   */
  #findMediaElementById(mediaId) {
    const selectors = [`[data-media-id="${mediaId}"]`, `[data-media-id$="-${mediaId}"]`];

    const candidates = Array.from(this.querySelectorAll(selectors.join(',')));

    for (const candidate of candidates) {
      if (!(candidate instanceof HTMLElement)) continue;
      if (candidate.closest('slideshow-controls')) continue;
      if (candidate.closest('.dialog-thumbnails-list')) continue;
      if (candidate.closest('[class*="thumbnail"]')) continue;

      return candidate;
    }

    return null;
  }

  /**
   * Finds the media index by media id.
   *
   * @param {string} mediaId
   * @param {HTMLElement[]} mediaItems
   * @returns {number}
   */
  #findMediaIndexById(mediaId, mediaItems) {
    return mediaItems.findIndex((media) => {
      if (!(media instanceof HTMLElement)) return false;

      const directMediaId = media.dataset.mediaId;
      const nestedMedia = media.querySelector('[data-media-id]');
      const nestedMediaId = nestedMedia instanceof HTMLElement ? nestedMedia.dataset.mediaId : '';

      return this.#valueMatchesMediaId(directMediaId, mediaId) || this.#valueMatchesMediaId(nestedMediaId, mediaId);
    });
  }

  /**
   * Gets all main gallery media items.
   *
   * @returns {HTMLElement[]}
   */
  #getMediaItems() {
    const slideshow = this.querySelector('slideshow-component');

    if (slideshow instanceof HTMLElement) {
      const slides = Array.from(slideshow.querySelectorAll('slideshow-slide, [ref="slides[]"]')).filter((item) => {
        if (!(item instanceof HTMLElement)) return false;
        if (item.closest('zoom-dialog')) return false;
        return Boolean(item.querySelector('[data-media-id]') || item.dataset.mediaId);
      });

      if (slides.length > 0) {
        return /** @type {HTMLElement[]} */ (slides);
      }
    }

    const refsMedia = Array.from(this.media || []).filter((item) => {
      if (!(item instanceof HTMLElement)) return false;
      if (item.closest('zoom-dialog')) return false;
      if (item.closest('dialog')) return false;
      return Boolean(item.querySelector('[data-media-id]') || item.dataset.mediaId);
    });

    if (refsMedia.length > 0) {
      return /** @type {HTMLElement[]} */ (refsMedia);
    }

    return Array.from(
      this.querySelectorAll(
        [
          '[ref="media[]"]',
          'slideshow-slide',
          '.slideshow-slide',
          '.product-media-container',
          '.product-media',
        ].join(',')
      )
    ).filter((item) => {
      if (!(item instanceof HTMLElement)) return false;
      if (item.closest('zoom-dialog')) return false;
      if (item.closest('dialog')) return false;
      if (item.closest('slideshow-controls')) return false;
      if (item.closest('.thumbnail-list')) return false;
      if (item.closest('[class*="thumbnail"]')) return false;
      return Boolean(item.querySelector('[data-media-id]') || item.dataset.mediaId);
    });
  }

  /**
   * Gets closest main media item from a nested media element.
   *
   * @param {HTMLElement} element
   * @returns {HTMLElement | null}
   */
  #getClosestMediaItem(element) {
    return (
      element.closest('slideshow-slide') ||
      element.closest('[ref="slides[]"]') ||
      element.closest('[ref="media[]"]') ||
      element.closest('.slideshow-slide') ||
      element.closest('.product-media-container') ||
      element.closest('.product-media')
    );
  }

  /**
   * Select slideshow index while preventing the page from jumping to the gallery.
   *
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #selectSlideshowIndexWithoutPageJump(index, options = {}) {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    this.#selectSlideshowIndex(index, options);

    this.#restoreWindowScroll(scrollX, scrollY);
  }

  /**
   * Select slideshow index if slideshow exists.
   * Fallback avoids scrollIntoView to prevent page jumps.
   *
   * @param {number} index
   * @param {{ animate?: boolean }} [options]
   */
  #selectSlideshowIndex(index, options = {}) {
    const animate = options.animate === true;

    if (this.slideshow && typeof this.slideshow.select === 'function') {
      this.slideshow.select(index, undefined, { animate });
      return;
    }

    const mediaItems = this.#getMediaItems();
    const selectedMedia = mediaItems[index];

    if (selectedMedia instanceof HTMLElement) {
      const slideshowScroller =
        selectedMedia.closest('slideshow-slides') ||
        selectedMedia.closest('[ref="scroller"]') ||
        selectedMedia.parentElement;

      if (slideshowScroller instanceof HTMLElement) {
        this.#centerElementInsideContainer(selectedMedia, slideshowScroller, { animate });
      }
    }
  }

  /**
   * Restores page scroll repeatedly to avoid theme slideshow/focus forcing page jump.
   *
   * @param {number} scrollX
   * @param {number} scrollY
   */
  #restoreWindowScroll(scrollX, scrollY) {
    const restore = () => {
      if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
        window.scrollTo(scrollX, scrollY);
      }
    };

    restore();

    requestAnimationFrame(() => {
      restore();
    });

    window.setTimeout(() => {
      restore();
    }, 50);

    window.setTimeout(() => {
      restore();
    }, 120);

    window.setTimeout(() => {
      restore();
    }, 220);
  }

  /**
   * Sync active thumbnail state and center the matched thumbnail.
   *
   * @param {string} mediaId
   * @param {number} index
   * @param {{ center?: boolean; animate?: boolean }} [options]
   */
  #syncActiveThumbnail(mediaId, index, options = {}) {
    const mainThumbnails = this.#getMainThumbnailCandidates();
    const dialogThumbnails = this.#getDialogThumbnailCandidates();

    const activeMainThumbnail = this.#syncThumbnailGroup(mainThumbnails, mediaId, index);
    this.#syncThumbnailGroup(dialogThumbnails, mediaId, index);

    if (activeMainThumbnail && options.center !== false) {
      this.#centerThumbnailRepeated(activeMainThumbnail, { animate: options.animate === true });
    }
  }

  /**
   * Sync thumbnail by index only.
   *
   * @param {number} index
   * @param {{ center?: boolean; animate?: boolean }} [options]
   */
  #syncActiveThumbnailByIndex(index, options = {}) {
    const mediaItems = this.#getMediaItems();
    const mediaItem = mediaItems[index];
    const mediaElement = mediaItem instanceof HTMLElement ? mediaItem.querySelector('[data-media-id]') : null;
    const mediaId = mediaElement instanceof HTMLElement ? mediaElement.dataset.mediaId || '' : '';

    const mainThumbnails = this.#getMainThumbnailCandidates();
    const dialogThumbnails = this.#getDialogThumbnailCandidates();

    const activeMainThumbnail = this.#syncThumbnailGroup(mainThumbnails, mediaId, index);
    this.#syncThumbnailGroup(dialogThumbnails, mediaId, index);

    if (activeMainThumbnail && options.center !== false) {
      this.#centerThumbnailRepeated(activeMainThumbnail, { animate: options.animate === true });
    }
  }

  /**
   * Syncs one group of thumbnails and returns the active thumbnail.
   *
   * @param {HTMLElement[]} thumbnails
   * @param {string} mediaId
   * @param {number} index
   * @returns {HTMLElement | null}
   */
  #syncThumbnailGroup(thumbnails, mediaId, index) {
    if (!thumbnails.length) return null;

    let activeThumbnail = null;

    const directMediaMatch = mediaId
      ? thumbnails.find((thumbnail) => this.#isThumbnailMediaIdMatch(thumbnail, mediaId))
      : undefined;

    const directIndexMatch = thumbnails.find((thumbnail) => this.#isThumbnailIndexMatch(thumbnail, index));

    const fallbackIndexMatch = thumbnails[index];

    const matchedThumbnail =
      directMediaMatch instanceof HTMLElement
        ? directMediaMatch
        : directIndexMatch instanceof HTMLElement
          ? directIndexMatch
          : fallbackIndexMatch instanceof HTMLElement
            ? fallbackIndexMatch
            : null;

    thumbnails.forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      const isMatch = thumbnail === matchedThumbnail;

      thumbnail.classList.toggle('is-active', isMatch);
      thumbnail.classList.toggle('active', isMatch);
      thumbnail.classList.toggle('selected', isMatch);
      thumbnail.classList.toggle('is-selected', isMatch);

      if (isMatch) {
        thumbnail.setAttribute('aria-selected', 'true');
        thumbnail.setAttribute('aria-current', 'true');
        activeThumbnail = thumbnail;
      } else {
        thumbnail.removeAttribute('aria-selected');
        thumbnail.removeAttribute('aria-current');
      }
    });

    return activeThumbnail;
  }

  /**
   * Gets main product slideshow thumbnail buttons only.
   *
   * @returns {HTMLElement[]}
   */
  #getMainThumbnailCandidates() {
    const slideshow = this.querySelector('slideshow-component');

    if (!(slideshow instanceof HTMLElement)) return [];

    const controls = Array.from(slideshow.querySelectorAll('slideshow-controls[thumbnails]')).filter((control) => {
      if (!(control instanceof HTMLElement)) return false;
      if (control.closest('zoom-dialog')) return false;
      return true;
    });

    const thumbnails = [];

    controls.forEach((control) => {
      const controlThumbnails = Array.from(
        control.querySelectorAll(
          [
            '[ref="thumbnails[]"]',
            '.slideshow-controls__thumbnail',
            'button[data-media-id]',
            'button[data-slide-index]',
          ].join(',')
        )
      ).filter((thumbnail) => {
        if (!(thumbnail instanceof HTMLElement)) return false;
        if (thumbnail.closest('zoom-dialog')) return false;
        if (thumbnail.closest('.dialog-thumbnails-list')) return false;
        return true;
      });

      thumbnails.push(...controlThumbnails);
    });

    return /** @type {HTMLElement[]} */ ([...new Set(thumbnails)]);
  }

  /**
   * Gets zoom dialog thumbnails.
   * These are synced for active state, but the main thumbnail track is what gets centered.
   *
   * @returns {HTMLElement[]}
   */
  #getDialogThumbnailCandidates() {
    const zoomDialog = this.querySelector('zoom-dialog');

    if (!(zoomDialog instanceof HTMLElement)) return [];

    const thumbnails = Array.from(
      zoomDialog.querySelectorAll(
        [
          '.dialog-thumbnails-list__thumbnail',
          '.dialog-thumbnails-list button[data-media-id]',
          '.dialog-thumbnails-list button[data-slide-index]',
        ].join(',')
      )
    ).filter((thumbnail) => thumbnail instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ ([...new Set(thumbnails)]);
  }

  /**
   * Checks if thumbnail media id matches selected media id.
   *
   * @param {HTMLElement} thumbnail
   * @param {string} mediaId
   * @returns {boolean}
   */
  #isThumbnailMediaIdMatch(thumbnail, mediaId) {
    if (!mediaId) return false;

    const values = [
      thumbnail.dataset.mediaId,
      thumbnail.getAttribute('data-media-id'),
      thumbnail.querySelector('[data-media-id]')?.getAttribute('data-media-id') || '',
    ].filter(Boolean);

    return values.some((value) => this.#valueMatchesMediaId(value, mediaId));
  }

  /**
   * Checks if thumbnail index matches selected media index.
   *
   * @param {HTMLElement} thumbnail
   * @param {number} index
   * @returns {boolean}
   */
  #isThumbnailIndexMatch(thumbnail, index) {
    const values = [
      thumbnail.dataset.slideIndex,
      thumbnail.getAttribute('data-slide-index'),
      thumbnail.dataset.index,
      thumbnail.getAttribute('data-index'),
    ].filter(Boolean);

    if (values.some((value) => value === String(index))) return true;

    const clickAction = thumbnail.getAttribute('on:click') || '';
    const selectMatch = clickAction.match(/\/(?:select|handleThumbnailClick)\/(\d+)/);

    return selectMatch?.[1] === String(index);
  }

  /**
   * Checks if a value matches a Shopify media id.
   *
   * @param {string | undefined | null} value
   * @param {string} mediaId
   * @returns {boolean}
   */
  #valueMatchesMediaId(value, mediaId) {
    if (!value || !mediaId) return false;

    return value === mediaId || value.endsWith(`-${mediaId}`);
  }

  /**
   * Centers selected thumbnail in its scroll track several times.
   * This prevents theme slideshow updates from overriding the first scroll position.
   *
   * @param {HTMLElement} thumbnail
   * @param {{ animate?: boolean }} [options]
   */
  #centerThumbnailRepeated(thumbnail, options = {}) {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    this.#centerThumbnail(thumbnail, options);
    this.#restoreWindowScroll(scrollX, scrollY);

    requestAnimationFrame(() => {
      this.#centerThumbnail(thumbnail, options);
      this.#restoreWindowScroll(scrollX, scrollY);
    });

    window.setTimeout(() => {
      this.#centerThumbnail(thumbnail, options);
      this.#restoreWindowScroll(scrollX, scrollY);
    }, 80);

    window.setTimeout(() => {
      this.#centerThumbnail(thumbnail, options);
      this.#restoreWindowScroll(scrollX, scrollY);
    }, 180);

    window.setTimeout(() => {
      this.#centerThumbnail(thumbnail, options);
      this.#restoreWindowScroll(scrollX, scrollY);
    }, 320);
  }

  /**
   * Centers selected thumbnail in its real scroll container.
   * This intentionally does NOT call scrollIntoView, because scrollIntoView can move the whole page.
   *
   * @param {HTMLElement} thumbnail
   * @param {{ animate?: boolean }} [options]
   */
  #centerThumbnail(thumbnail, options = {}) {
    const animate = options.animate === true;
    const scrollContainer = this.#getThumbnailScrollContainer(thumbnail);

    if (!(scrollContainer instanceof HTMLElement)) return;

    this.#centerElementInsideContainer(thumbnail, scrollContainer, { animate });
  }

  /**
   * Centers one element inside one scroll container with boundary clamp.
   *
   * Behavior:
   * - If the selected thumbnail can be visually centered, center it.
   * - If it is near the start, keep the scroll at the start.
   * - If it is near the end, keep the scroll at the end.
   * - This avoids empty blank space at the top/left or bottom/right.
   *
   * Only updates the container scrollLeft/scrollTop. It never scrolls the page.
   *
   * @param {HTMLElement} element
   * @param {HTMLElement} container
   * @param {{ animate?: boolean }} [options]
   */
  #centerElementInsideContainer(element, container, options = {}) {
    const animate = options.animate === true;
    const behavior = animate ? 'smooth' : 'auto';

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const canScrollX = container.scrollWidth > container.clientWidth + 1;
    const canScrollY = container.scrollHeight > container.clientHeight + 1;

    if (canScrollX) {
      const currentLeft = container.scrollLeft;
      const elementCenterX = elementRect.left - containerRect.left + currentLeft + elementRect.width / 2;
      const rawTargetLeft = elementCenterX - container.clientWidth / 2;
      const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
      const targetLeft = Math.min(Math.max(0, rawTargetLeft), maxLeft);

      container.scrollTo({
        left: targetLeft,
        behavior,
      });
    }

    if (canScrollY) {
      const currentTop = container.scrollTop;
      const elementCenterY = elementRect.top - containerRect.top + currentTop + elementRect.height / 2;
      const rawTargetTop = elementCenterY - container.clientHeight / 2;
      const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
      const targetTop = Math.min(Math.max(0, rawTargetTop), maxTop);

      container.scrollTo({
        top: targetTop,
        behavior,
      });
    }
  }

  /**
   * Finds the real scroll container for the thumbnail.
   *
   * @param {HTMLElement} thumbnail
   * @returns {HTMLElement | null}
   */
  #getThumbnailScrollContainer(thumbnail) {
    const preferred =
      thumbnail.closest('.slideshow-controls__thumbnails-container') ||
      thumbnail.closest('.dialog-thumbnails-list') ||
      thumbnail.closest('.thumbnail-list');

    if (preferred instanceof HTMLElement) {
      return preferred;
    }

    let parent = thumbnail.parentElement;

    while (parent && parent !== this) {
      const style = window.getComputedStyle(parent);
      const canScrollX = parent.scrollWidth > parent.clientWidth + 1;
      const canScrollY = parent.scrollHeight > parent.clientHeight + 1;
      const allowsScrollX = ['auto', 'scroll', 'overlay', 'hidden'].includes(style.overflowX);
      const allowsScrollY = ['auto', 'scroll', 'overlay', 'hidden'].includes(style.overflowY);

      if ((canScrollX && allowsScrollX) || (canScrollY && allowsScrollY)) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return null;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}